import React from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { CheckCircle, ShoppingBag, Phone, Mail, MapPin, ArrowRight } from 'lucide-react';

export const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const order = location.state?.order;

  return (
    <div className="min-h-screen bg-[#FDF8F3] py-12">
      <div className="max-w-3xl mx-auto px-6">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-4">
            <CheckCircle className="text-green-600" size={48} />
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#2C1810] mb-4">
            Commande confirmée !
          </h1>
          <p className="text-xl text-[#5D4037] font-semibold mb-2">
            ✅ Votre commande a bien été validée
          </p>
          <p className="text-lg text-[#5D4037]">
            Vous recevrez un email de confirmation. Le paiement s'effectuera en magasin lors du retrait.
          </p>
        </div>

        {/* Order Details */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-6">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-[#E8DDD4]">
            <div>
              <p className="text-sm text-[#A67B5B] mb-1">Numéro de commande</p>
              <p className="text-2xl font-bold text-[#2C1810]">
                {order?.orderNumber || orderId?.substring(0, 8).toUpperCase()}
              </p>
            </div>
            {order?.total && (
              <div className="text-right">
                <p className="text-sm text-[#A67B5B] mb-1">Montant total</p>
                <p className="text-3xl font-bold text-[#C9A96E]">
                  {order.total.toFixed(2)}€
                </p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Pickup Info */}
            <div className="p-6 bg-[#F5F0EB] rounded-lg">
              <h3 className="font-semibold text-[#2C1810] mb-4 flex items-center gap-2">
                <ShoppingBag className="text-[#C9A96E]" size={20} />
                Retrait en magasin
              </h3>
              <div className="space-y-3 text-[#5D4037]">
                <div className="flex items-start gap-3">
                  <MapPin className="text-[#A67B5B] flex-shrink-0 mt-1" size={18} />
                  <div>
                    <p className="font-semibold">Miss Baleng</p>
                    <p className="text-sm">1 rue Albert de Mun</p>
                    <p className="text-sm">92600 Asnières-sur-Seine</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="text-[#A67B5B] flex-shrink-0 mt-1" size={18} />
                  <div>
                    <p className="text-sm">Nous vous appellerons quand votre commande sera prête</p>
                    <p className="font-semibold">06 19 21 17 90</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div>
              <h3 className="font-semibold text-[#2C1810] mb-4">Prochaines étapes</h3>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#C9A96E] text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C1810]">Préparation de votre commande</p>
                    <p className="text-sm text-[#5D4037]">
                      Notre équipe prépare votre commande avec soin
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#C9A96E] text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C1810]">Notification de disponibilité</p>
                    <p className="text-sm text-[#5D4037]">
                      Nous vous appelons dès que votre commande est prête
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#C9A96E] text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C1810]">Retrait en magasin</p>
                    <p className="text-sm text-[#5D4037]">
                      Venez récupérer et payer votre commande en magasin
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Info */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex gap-3">
                <Mail className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <p className="font-semibold text-blue-900 mb-1">Email de confirmation</p>
                  <p className="text-sm text-blue-700">
                    Un email de confirmation vous a été envoyé avec les détails de votre commande.
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-400 rounded-lg">
              <p className="text-lg text-amber-900 text-center font-bold mb-2">
                💳 PAIEMENT EN MAGASIN LORS DU RETRAIT
              </p>
              <p className="text-sm text-amber-800 text-center">
                Vous réglerez votre commande directement en magasin au moment de la récupération<br />
                <strong>Espèces et carte bancaire acceptées</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/produits"
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-[#C9A96E] text-white rounded-full hover:bg-[#A67B5B] transition-colors font-semibold text-center"
          >
            Continuer mes achats
            <ArrowRight size={20} />
          </Link>
          <Link
            to="/"
            className="flex-1 px-6 py-4 border-2 border-[#D4C4B5] text-[#2C1810] rounded-full hover:bg-[#F5F0EB] transition-colors font-semibold text-center"
          >
            Retour à l'accueil
          </Link>
        </div>

        {/* Contact Info */}
        <div className="mt-8 text-center text-sm text-[#5D4037]">
          <p className="mb-2">Des questions sur votre commande ?</p>
          <p>
            Contactez-nous au <a href="tel:0619211790" className="text-[#C9A96E] hover:underline font-semibold">06 19 21 17 90</a>
            {' '}ou par email à <a href="mailto:contact@missbaleng.com" className="text-[#C9A96E] hover:underline font-semibold">contact@missbaleng.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};
